import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Grid,
  Typography,
  Slide,
  Zoom,
  Divider,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Sync as SyncIcon,
  Euro as EuroIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  AppLayout,
  StatCard,
  StatusCard,
} from './UI';
import { Button } from '@mui/material';
import { AppState } from '@/types';
import { appStateService } from '@/services/appStateService';
import { syncService } from '@/services/syncService';
import { notificationService } from '@/services/notificationService';
import { apiService } from '@/services/api';

interface DashboardProps {
  onCreateDonation: () => void;
  onViewDonations: () => void;
  onElectronicDonation: () => void;
  onNavigateToCredits?: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  onCreateDonation,
  onViewDonations,
  onElectronicDonation,
  onNavigateToCredits,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [appState, setAppState] = useState<AppState>(appStateService.getState());
  const [isSyncing, setIsSyncing] = useState(false);
  const [stats, setStats] = useState({
    pendingDonations: 0,
    pendingReceipts: 0,
    totalOfflineDonations: 0,
    totalOfflineReceipts: 0,
  });
  const [donationStats, setDonationStats] = useState({
    totalCount: 0,
    totalAmount: 0,
    averageAmount: 0,
    confirmedCount: 0,
    confirmedAmount: 0,
    pendingCount: 0,
    pendingAmount: 0,
    recentDonations: [],
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [activeBlock, setActiveBlock] = useState<'donations' | 'amounts'>('donations');
  const [isAnimating, setIsAnimating] = useState(false);

  const handleBlockChange = (newBlock: 'donations' | 'amounts') => {
    if (isAnimating || activeBlock === newBlock) return;
    
    setIsAnimating(true);
    setTimeout(() => {
      setActiveBlock(newBlock);
      setTimeout(() => {
        setIsAnimating(false);
      }, 200);
    }, 250);
  };

  useEffect(() => {
    // Listener per aggiornamenti stato app
    const handleStateChange = (newState: AppState) => {
      setAppState(newState);
    };

    // Listener per aggiornamenti dati dashboard dal sync
    const handleDashboardDataUpdate = (event: CustomEvent) => {
      console.log('📊 Dashboard received data update event:', event.detail);
      console.log('📊 Reloading dashboard stats...');
      // Ricarica le statistiche quando il sync è completato
      loadStats();
      loadDonationStats();
    };

    appStateService.addStateListener(handleStateChange);
    window.addEventListener('dashboardDataUpdate', handleDashboardDataUpdate as EventListener);

    // Carica dati iniziali
    loadStats();
    loadDonationStats();

    return () => {
      appStateService.removeStateListener(handleStateChange);
      window.removeEventListener('dashboardDataUpdate', handleDashboardDataUpdate as EventListener);
    };
  }, []);


  const loadStats = async () => {
    try {
      console.log('📊 Loading offline stats...');
      const offlineStats = await syncService.getOfflineStats();
      console.log('📊 Offline stats loaded:', offlineStats);
      setStats(offlineStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadDonationStats = async () => {
    if (!appState.isAuthenticated || !appState.isOnline) {
      console.log('⚠️ Skipping donation stats load - not authenticated or offline');
      return;
    }
    
    setIsLoadingStats(true);
    try {
      console.log('📊 Loading donation stats...');
      const stats = await apiService.getMyDonationStats();
      console.log('📊 Donation stats loaded:', stats);
      setDonationStats(stats);
    } catch (error) {
      console.error('❌ Error loading donation stats:', error);
      // Reset stats in case of error
      setDonationStats({
        totalCount: 0,
        totalAmount: 0,
        averageAmount: 0,
        confirmedCount: 0,
        confirmedAmount: 0,
        pendingCount: 0,
        pendingAmount: 0,
        recentDonations: [],
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleSync = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    notificationService.syncStarted();

    try {
      const result = await syncService.forceSync();
      
      if (result.success) {
        notificationService.syncCompleted(result.syncedDonations, result.syncedReceipts);
      } else {
        notificationService.syncFailed(result.errors);
      }

      // Aggiorna dati
      await loadStats();
      
      // Emetti evento per aggiornare altri componenti
      console.log('📡 Dashboard dispatching dashboardDataUpdate event...');
      const event = new CustomEvent('dashboardDataUpdate', {
        detail: { 
          timestamp: Date.now(),
          currentView: 'dashboard',
          syncedDonations: result.syncedDonations,
          syncedReceipts: result.syncedReceipts
        }
      });
      window.dispatchEvent(event);
      console.log('📡 Dashboard event dispatched:', event.detail);
    } catch (error) {
      console.error('Sync failed:', error);
      notificationService.error('Sincronizzazione fallita', 'Errore durante la sincronizzazione');
    } finally {
      setIsSyncing(false);
    }
  };



  return (
    <AppLayout
      title="Dashboard"
      showSyncButton={true}
      onSync={handleSync}
      currentView="dashboard"
      onNavigateToCredits={onNavigateToCredits}
      onNavigate={(view) => {
        if (view === 'new-donation') {
          onCreateDonation();
        } else if (view === 'donations-list') {
          onViewDonations();
        } else if (view === 'electronic-donation') {
          onElectronicDonation();
        }
      }}
    >
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto'
      }}>
        {/* Header con bottoni */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          mb: isMobile ? 2 : 3,
          px: isMobile ? 1 : 0,
          gap: isMobile ? 1 : 2,
          flexDirection: 'row',
          flexWrap: 'wrap'
        }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateDonation}
          size={isMobile ? "medium" : "large"}
          sx={{ 
            borderRadius: isMobile ? 1.5 : 2,
            px: isMobile ? 2 : 3,
            py: isMobile ? 1 : 1.5,
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            flex: '1 1 0',
            minWidth: isMobile ? '140px' : '200px',
            width: '100%'
          }}
        >
          Nuova<br/>Donazione
        </Button>
        <Button
          variant="outlined"
          startIcon={<PaymentIcon />}
          onClick={onElectronicDonation}
          size={isMobile ? "medium" : "large"}
          sx={{ 
            borderRadius: isMobile ? 1.5 : 2,
            px: isMobile ? 2 : 3,
            py: isMobile ? 1 : 1.5,
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            flex: '1 1 0',
            minWidth: isMobile ? '140px' : '200px',
            width: '100%',
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.light',
              color: 'primary.dark'
            }
          }}
        >
          Donazione<br/>Elettronica
        </Button>
        </Box>

        {/* Contenuto principale - si espande per riempire lo spazio */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%'
        }}>
          <Grid container spacing={3} justifyContent="center">
        {/* Container per animazioni fluide */}
        <Grid item xs={12} sm={11} md={10} lg={9} xl={8}>
          <Box sx={{ 
            position: 'relative', 
            minHeight: isMobile ? 180 : isTablet ? 200 : 220,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%'
          }}>
            {/* Blocco Numero Donazioni */}
            <Slide 
              direction="right" 
              in={activeBlock === 'donations' && !isAnimating} 
              timeout={400}
              easing="cubic-bezier(0.4, 0, 0.2, 1)"
            >
              <Box
                sx={{
                  position: activeBlock === 'donations' ? 'static' : 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  width: '100%',
                  maxWidth: isMobile ? '100%' : isTablet ? '90%' : '80%',
                  mx: 'auto',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: isMobile ? 1.5 : 2,
                  p: isMobile ? 1.5 : 2,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  cursor: 'pointer',
                  transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transform: activeBlock === 'donations' ? 'scale(1) rotate(0deg)' : 'scale(0.9) rotate(2deg)',
                  opacity: activeBlock === 'donations' ? 1 : 0,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'scale(1.02) translateY(-4px)',
                    borderColor: 'primary.dark',
                  }
                }}
                onClick={() => handleBlockChange('amounts')}
              >
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{ 
                    mb: isMobile ? 1.5 : 2, 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'color 0.3s ease',
                    textAlign: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}
                >
                  <AssessmentIcon sx={{ mr: 1, fontSize: '1.2em' }} />
                  {isMobile ? 'Donazioni' : 'Numero Donazioni del Mese'}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      ml: isMobile ? 0 : 'auto', 
                      opacity: 0.7,
                      transition: 'opacity 0.3s ease',
                      '&:hover': { opacity: 1 },
                      display: isMobile ? 'block' : 'inline',
                      textAlign: 'center',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {isMobile ? 'Clicca per importi →' : 'Clicca per vedere gli importi →'}
                  </Typography>
                </Typography>
                <Grid container spacing={isMobile ? 0.5 : 1} justifyContent="center">
                  <Grid item xs={6} sm={3}>
                    <Zoom in={activeBlock === 'donations' && !isAnimating} timeout={500} style={{ transitionDelay: '100ms' }}>
                      <Box>
                        <StatCard
                          title="Confermate"
                          value={isLoadingStats ? '...' : donationStats.confirmedCount}
                          icon={<CheckCircleIcon />}
                          color="success"
                        />
                      </Box>
                    </Zoom>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Zoom in={activeBlock === 'donations' && !isAnimating} timeout={500} style={{ transitionDelay: '200ms' }}>
                      <Box>
                        <StatCard
                          title="In Attesa"
                          value={isLoadingStats ? '...' : donationStats.pendingCount}
                          icon={<WarningIcon />}
                          color="warning"
                        />
                      </Box>
                    </Zoom>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Zoom in={activeBlock === 'donations' && !isAnimating} timeout={500} style={{ transitionDelay: '300ms' }}>
                      <Box>
                        <StatCard
                          title="Totale"
                          value={isLoadingStats ? '...' : donationStats.totalCount}
                          icon={<TrendingUpIcon />}
                          color="info"
                        />
                      </Box>
                    </Zoom>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Zoom in={activeBlock === 'donations' && !isAnimating} timeout={500} style={{ transitionDelay: '400ms' }}>
                      <Box>
                        <StatCard
                          title="Da inviare"
                          value={stats.pendingDonations + stats.pendingReceipts}
                          icon={<SyncIcon />}
                          color="info"
                        />
                      </Box>
                    </Zoom>
                  </Grid>
                </Grid>
              </Box>
            </Slide>

            {/* Blocco Importi */}
            <Slide 
              direction="left" 
              in={activeBlock === 'amounts' && !isAnimating} 
              timeout={400}
              easing="cubic-bezier(0.4, 0, 0.2, 1)"
            >
              <Box
                sx={{
                  position: activeBlock === 'amounts' ? 'static' : 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  width: '100%',
                  maxWidth: isMobile ? '100%' : isTablet ? '90%' : '80%',
                  mx: 'auto',
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: isMobile ? 1.5 : 2,
                  p: isMobile ? 1.5 : 2,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  cursor: 'pointer',
                  transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transform: activeBlock === 'amounts' ? 'scale(1) rotate(0deg)' : 'scale(0.9) rotate(-2deg)',
                  opacity: activeBlock === 'amounts' ? 1 : 0,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'scale(1.02) translateY(-4px)',
                    borderColor: 'success.dark',
                  }
                }}
                onClick={() => handleBlockChange('donations')}
              >
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{ 
                    mb: isMobile ? 1.5 : 2, 
                    fontWeight: 'bold', 
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'color 0.3s ease',
                    textAlign: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}
                >
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: '1.2em' }} />
                  {isMobile ? 'Importi' : 'Importi del Mese'}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      ml: isMobile ? 0 : 'auto', 
                      opacity: 0.7,
                      transition: 'opacity 0.3s ease',
                      '&:hover': { opacity: 1 },
                      display: isMobile ? 'block' : 'inline',
                      textAlign: 'center',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {isMobile ? 'Clicca per numeri →' : 'Clicca per vedere i numeri →'}
                  </Typography>
                </Typography>
                <Grid container spacing={isMobile ? 0.5 : 1} justifyContent="center">
                  <Grid item xs={6} sm={3}>
                    <Zoom in={activeBlock === 'amounts' && !isAnimating} timeout={500} style={{ transitionDelay: '100ms' }}>
                      <Box>
                        <StatCard
                          title="Confermate"
                          value={isLoadingStats ? '...' : `€${donationStats.confirmedAmount.toFixed(2)}`}
                          icon={<EuroIcon />}
                          color="success"
                        />
                      </Box>
                    </Zoom>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Zoom in={activeBlock === 'amounts' && !isAnimating} timeout={500} style={{ transitionDelay: '200ms' }}>
                      <Box>
                        <StatCard
                          title="In Attesa"
                          value={isLoadingStats ? '...' : `€${donationStats.pendingAmount.toFixed(2)}`}
                          icon={<EuroIcon />}
                          color="warning"
                        />
                      </Box>
                    </Zoom>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Zoom in={activeBlock === 'amounts' && !isAnimating} timeout={500} style={{ transitionDelay: '300ms' }}>
                      <Box>
                        <StatCard
                          title="Totale"
                          value={isLoadingStats ? '...' : `€${donationStats.totalAmount.toFixed(2)}`}
                          icon={<EuroIcon />}
                          color="primary"
                        />
                      </Box>
                    </Zoom>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Zoom in={activeBlock === 'amounts' && !isAnimating} timeout={500} style={{ transitionDelay: '400ms' }}>
                      <Box>
                        <StatCard
                          title="Media"
                          value={isLoadingStats ? '...' : `€${donationStats.averageAmount.toFixed(2)}`}
                          icon={<EuroIcon />}
                          color="info"
                        />
                      </Box>
                    </Zoom>
                  </Grid>
                </Grid>
              </Box>
            </Slide>
          </Box>
        </Grid>


        {/* Stato connessione */}
        {!appState.isOnline && (
          <Grid item xs={12}>
            <StatusCard
              title="Modalità Offline"
              status="offline"
              message="L'app funziona in modalità offline"
              details="Le donazioni verranno sincronizzate automaticamente quando tornerai online"
            />
          </Grid>
        )}

        {/* Footer Fondazione */}
        <Grid item xs={12}>
          <Box sx={{ mt: 4, mb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ 
              textAlign: 'center',
              opacity: 0.7,
              '&:hover': { opacity: 1 }
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  fontSize: isMobile ? '0.65rem' : '0.7rem',
                  color: 'primary.main',
                  lineHeight: 1.4
                }}
              >
                <strong>Fondazione Roberta Mucera ETS</strong>
                <br />
                Ente del Terzo Settore - Repertorio n. 144574
                <br />
                C.F. e P.IVA: 97384690828
                <br />
                Via Poggio Ridente, 8 - 90131 PALERMO (PA) - ITALIA
                <br />
                PEC: fondazionerobertamucera@pec.it
                <br />
                <Link 
                  href="https://www.fondazionerobertamucera.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ 
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  www.fondazionerobertamucera.org
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>

          </Grid>
        </Box>

        {/* Footer - sempre in basso */}
        <Box sx={{ 
          mt: 'auto',
          pt: 2,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          // bgcolor: 'background.paper'
        }}>
          <Typography variant="caption" color="primary.main">
          © 2025 D-Service Italia - Tutti i diritti riservati
          </Typography>
        </Box>
      </Box>
    </AppLayout>
  );
};
